'use client';

import React from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';

interface AppLogoProps {
  src?: string; // Image source (optional)
  text?: string; // Logo text (optional)
  iconName?: string; // Icon name when no image
  size?: number; // Size for icon/image
  className?: string; // Additional classes
  onClick?: () => void; // Click handler
  variant?: 'default' | 'option1' | 'option2' | 'option3' | 'option4' | 'option5' | 'option6' | 'option7' | 'option8' | 'option9'; // Logo design variant
}

function AppLogo({
  src = '/assets/images/app_logo.png',
  text,
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
  variant = 'default',
}: AppLogoProps) {
  // Render custom SVG logos based on variant
  const renderCustomLogo = () => {
    const logoSize = size;
    const colors = {
      crimson: '#ff004c',
      gold: '#d4af37',
      emerald: '#10B981',
      purple: '#8B5CF6',
      deepBlack: '#0a0a0a',
      white: '#ffffff',
    };

    switch (variant) {
      case 'option1':
        // Option 1: NFC Wave + Book Icon (Technology meets Story)
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Book/Tale element */}
            <path d="M18 12C18 10.8954 18.8954 10 20 10H44C45.1046 10 46 10.8954 46 12V52C46 53.1046 45.1046 54 44 54H20C18.8954 54 18 53.1046 18 52V12Z" fill={colors.crimson} fillOpacity="0.2" stroke={colors.crimson} strokeWidth="2"/>
            <path d="M22 18H42M22 26H42M22 34H38" stroke={colors.crimson} strokeWidth="2" strokeLinecap="round"/>
            {/* NFC Tap waves */}
            <circle cx="32" cy="32" r="16" stroke={colors.gold} strokeWidth="2" opacity="0.4"/>
            <circle cx="32" cy="32" r="22" stroke={colors.gold} strokeWidth="1.5" opacity="0.25"/>
            <circle cx="32" cy="32" r="28" stroke={colors.gold} strokeWidth="1" opacity="0.15"/>
            {/* Center tap point */}
            <circle cx="32" cy="32" r="4" fill={colors.gold}/>
          </svg>
        );

      case 'option2':
        // Option 2: Tag with Story Ribbon (Physical tag meets narrative)
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Tag shape */}
            <path d="M12 12L32 12C40 12 46 18 46 26L46 38C46 46 40 52 32 52L12 52L12 12Z" fill={colors.crimson} fillOpacity="0.15" stroke={colors.crimson} strokeWidth="2.5" strokeLinejoin="round"/>
            {/* Tag hole */}
            <circle cx="20" cy="20" r="3" fill={colors.crimson}/>
            {/* Story ribbon/scroll */}
            <path d="M28 24C28 24 34 22 38 24C42 26 42 30 38 32C34 34 28 32 28 32" stroke={colors.gold} strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M28 36C28 36 34 34 38 36C42 38 42 42 38 44C34 46 28 44 28 44" stroke={colors.gold} strokeWidth="2.5" strokeLinecap="round"/>
            {/* NFC indicator */}
            <path d="M50 28L54 32L50 36" stroke={colors.emerald} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );

      case 'option3':
        // Option 3: Fingerprint + Bookmark (Unique identity meets collection)
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Bookmark shape */}
            <path d="M20 8C20 6.89543 20.8954 6 22 6H42C43.1046 6 44 6.89543 44 8V56L32 48L20 56V8Z" fill={colors.crimson} fillOpacity="0.2" stroke={colors.crimson} strokeWidth="2.5"/>
            {/* Fingerprint/unique identity lines */}
            <path d="M26 20C26 20 28 18 32 18C36 18 38 20 38 20" stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            <path d="M24 26C24 26 26 22 32 22C38 22 40 26 40 26" stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            <path d="M26 32C26 32 28 28 32 28C36 28 38 32 38 32" stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            <path d="M28 38C28 38 30 34 32 34C34 34 36 38 36 38" stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            {/* Tap indicator dot */}
            <circle cx="32" cy="16" r="2.5" fill={colors.emerald}/>
          </svg>
        );

      case 'option4':
        // Option 4: Treasure Chest with Signal (Heritage meets Technology)
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Chest base */}
            <rect x="14" y="32" width="36" height="20" rx="2" fill={colors.crimson} fillOpacity="0.2" stroke={colors.crimson} strokeWidth="2.5"/>
            {/* Chest lid */}
            <path d="M14 32C14 32 14 20 32 20C50 20 50 32 50 32" fill={colors.crimson} fillOpacity="0.15" stroke={colors.crimson} strokeWidth="2.5"/>
            {/* Lock/clasp */}
            <circle cx="32" cy="32" r="4" fill={colors.gold}/>
            <rect x="30" y="32" width="4" height="8" fill={colors.gold}/>
            {/* Signal waves emanating */}
            <path d="M24 14C24 14 28 10 32 10C36 10 40 14 40 14" stroke={colors.emerald} strokeWidth="2" strokeLinecap="round" opacity="0.6"/>
            <path d="M20 18C20 18 26 12 32 12C38 12 44 18 44 18" stroke={colors.emerald} strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
          </svg>
        );

      case 'option5':
        // Premium Badge Style: Circular badge with NFC wave + book, elegant border
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer decorative circle */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="1.5" opacity="0.4"/>
            {/* Main badge circle */}
            <circle cx="32" cy="32" r="26" fill={colors.deepBlack} stroke={colors.gold} strokeWidth="2"/>
            {/* Inner decorative ring */}
            <circle cx="32" cy="32" r="22" stroke={colors.crimson} strokeWidth="0.5" opacity="0.3"/>
            
            {/* Book element (left side) */}
            <path d="M20 28C20 27.4 20.4 27 21 27H26C26.6 27 27 27.4 27 28V36C27 36.6 26.6 37 26 37H21C20.4 37 20 36.6 20 36V28Z" 
                  fill={colors.crimson} fillOpacity="0.3" stroke={colors.crimson} strokeWidth="1.5"/>
            <line x1="22" y1="30" x2="25" y2="30" stroke={colors.crimson} strokeWidth="0.8"/>
            <line x1="22" y1="32" x2="25" y2="32" stroke={colors.crimson} strokeWidth="0.8"/>
            <line x1="22" y1="34" x2="24" y2="34" stroke={colors.crimson} strokeWidth="0.8"/>
            
            {/* NFC waves (right side) */}
            <path d="M37 28C37 28 40 30 40 32C40 34 37 36 37 36" stroke={colors.gold} strokeWidth="1.8" strokeLinecap="round"/>
            <path d="M41 26C41 26 45 28.5 45 32C45 35.5 41 38 41 38" stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
            
            {/* Center tap point */}
            <circle cx="32" cy="32" r="2.5" fill={colors.gold}/>
            
            {/* Corner decorative elements */}
            <circle cx="32" cy="8" r="1.5" fill={colors.gold} opacity="0.6"/>
            <circle cx="32" cy="56" r="1.5" fill={colors.gold} opacity="0.6"/>
          </svg>
        );

      case 'option6':
        // Wax Seal Stamp: Authentic wax seal with TT monogram
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Wax seal outer edge (irregular) */}
            <path d="M32 4C38 4 44 6 48 10C52 14 56 20 58 26C60 32 60 38 58 44C56 50 52 54 48 56C44 58 38 60 32 60C26 60 20 58 16 56C12 54 8 50 6 44C4 38 4 32 6 26C8 20 12 14 16 10C20 6 26 4 32 4Z" 
                  fill={colors.crimson} fillOpacity="0.9"/>
            {/* Wax texture rings */}
            <circle cx="32" cy="32" r="24" stroke={colors.deepBlack} strokeWidth="0.5" opacity="0.2"/>
            <circle cx="32" cy="32" r="20" stroke={colors.deepBlack} strokeWidth="0.5" opacity="0.15"/>
            <circle cx="32" cy="32" r="16" stroke={colors.deepBlack} strokeWidth="0.5" opacity="0.1"/>
            
            {/* TT Monogram with tap symbol */}
            <text x="20" y="38" fontFamily="serif" fontSize="20" fontWeight="bold" fill={colors.gold}>T</text>
            <text x="36" y="38" fontFamily="serif" fontSize="20" fontWeight="bold" fill={colors.gold}>T</text>
            
            {/* Tap indicator between letters */}
            <circle cx="32" cy="32" r="3" fill={colors.gold} opacity="0.4"/>
            <circle cx="32" cy="32" r="1.5" fill={colors.gold}/>
            
            {/* Decorative seal marks */}
            <path d="M32 12L33 16L32 16L31 16L32 12Z" fill={colors.gold} opacity="0.5"/>
            <path d="M32 52L33 48L32 48L31 48L32 52Z" fill={colors.gold} opacity="0.5"/>
            <path d="M12 32L16 31L16 32L16 33L12 32Z" fill={colors.gold} opacity="0.5"/>
            <path d="M52 32L48 31L48 32L48 33L52 32Z" fill={colors.gold} opacity="0.5"/>
          </svg>
        );

      case 'option7':
        // Minimalist Emblem: Clean geometric badge with tap + book
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Hexagonal badge shape */}
            <path d="M32 6L50 16V48L32 58L14 48V16L32 6Z" 
                  fill={colors.deepBlack} stroke={colors.gold} strokeWidth="2" strokeLinejoin="miter"/>
            {/* Inner hex */}
            <path d="M32 12L44 19V45L32 52L20 45V19L32 12Z" 
                  stroke={colors.crimson} strokeWidth="1" opacity="0.3"/>
            
            {/* Minimalist book icon */}
            <rect x="24" y="26" width="10" height="12" rx="1" 
                  fill="none" stroke={colors.crimson} strokeWidth="1.5"/>
            <line x1="26" y1="29" x2="32" y2="29" stroke={colors.crimson} strokeWidth="1"/>
            <line x1="26" y1="32" x2="32" y2="32" stroke={colors.crimson} strokeWidth="1"/>
            <line x1="26" y1="35" x2="30" y2="35" stroke={colors.crimson} strokeWidth="1"/>
            
            {/* Tap gesture icon */}
            <circle cx="40" cy="32" r="1.5" fill={colors.gold}/>
            <path d="M40 28L40 36" stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M36 32L44 32" stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round"/>
            
            {/* Corner accents */}
            <line x1="32" y1="8" x2="32" y2="11" stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="32" y1="53" x2="32" y2="56" stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );

      case 'option8':
        // Heritage Crest: Classic crest with NFC waves and scroll
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Shield/crest shape */}
            <path d="M32 6C32 6 48 10 48 18V36C48 48 32 58 32 58C32 58 16 48 16 36V18C16 10 32 6 32 6Z" 
                  fill={colors.deepBlack} stroke={colors.gold} strokeWidth="2"/>
            {/* Inner shield detail */}
            <path d="M32 12C32 12 44 15 44 20V34C44 43 32 51 32 51C32 51 20 43 20 34V20C20 15 32 12 32 12Z" 
                  stroke={colors.crimson} strokeWidth="1" opacity="0.4"/>
            
            {/* Scroll/tale element */}
            <path d="M22 26C22 24 23 23 24 23H40C41 23 42 24 42 26V30C42 31 41 32 40 32H24C23 32 22 31 22 30V26Z" 
                  fill={colors.crimson} fillOpacity="0.2" stroke={colors.crimson} strokeWidth="1.5"/>
            <line x1="25" y1="27" x2="39" y2="27" stroke={colors.crimson} strokeWidth="0.8"/>
            <line x1="25" y1="29" x2="39" y2="29" stroke={colors.crimson} strokeWidth="0.8"/>
            
            {/* NFC waves emanating from center */}
            <circle cx="32" cy="40" r="3" stroke={colors.gold} strokeWidth="1.5"/>
            <circle cx="32" cy="40" r="6" stroke={colors.gold} strokeWidth="1" opacity="0.5"/>
            <circle cx="32" cy="40" r="9" stroke={colors.gold} strokeWidth="0.8" opacity="0.3"/>
            
            {/* Center tap point */}
            <circle cx="32" cy="40" r="1.5" fill={colors.gold}/>
            
            {/* Ornate top detail */}
            <circle cx="32" cy="10" r="2" fill={colors.gold}/>
          </svg>
        );

      case 'option9':
        // Modern Stamp Badge: Contemporary rounded stamp with tap + tale icon
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Rounded square stamp */}
            <rect x="8" y="8" width="48" height="48" rx="8" 
                  fill={colors.deepBlack} stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner stamp border */}
            <rect x="12" y="12" width="40" height="40" rx="6" 
                  stroke={colors.crimson} strokeWidth="1" opacity="0.4"/>
            
            {/* Perforated edge effect */}
            <circle cx="16" cy="16" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="48" cy="16" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="16" cy="48" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="48" cy="48" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="32" cy="12" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="32" cy="52" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="12" cy="32" r="1" fill={colors.gold} opacity="0.5"/>
            <circle cx="52" cy="32" r="1" fill={colors.gold} opacity="0.5"/>
            
            {/* Open book with tap symbol */}
            <path d="M20 28V38L32 36L44 38V28L32 30L20 28Z" 
                  fill={colors.crimson} fillOpacity="0.2" stroke={colors.crimson} strokeWidth="1.5"/>
            <line x1="32" y1="30" x2="32" y2="36" stroke={colors.crimson} strokeWidth="1"/>
            
            {/* Tap/touch icon above book */}
            <circle cx="32" cy="24" r="2" fill={colors.gold}/>
            <path d="M28 22C28 22 30 20 32 20C34 20 36 22 36 22" 
                  stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
            
            {/* Signal waves */}
            <path d="M26 40C26 40 29 42 32 42C35 42 38 40 38 40" 
                  stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        );

      case 'option10':
        // Original Shape + NFC Wave Signal Icon
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer gold ring */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner crimson ring */}
            <circle cx="32" cy="32" r="26" stroke={colors.crimson} strokeWidth="2"/>
            {/* Background fill */}
            <circle cx="32" cy="32" r="24" fill={colors.deepBlack}/>
            
            {/* NFC Wave Signal Icon (center) */}
            <path d="M24 24C24 24 26 22 32 22C38 22 40 24 40 24" 
                  stroke={colors.gold} strokeWidth="2.5" strokeLinecap="round"/>
            <path d="M20 28C20 28 24 18 32 18C40 18 44 28 44 28" 
                  stroke={colors.gold} strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
            <path d="M18 32C18 32 22 14 32 14C42 14 46 32 46 32" 
                  stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
            
            {/* Center tap point */}
            <circle cx="32" cy="38" r="4" fill={colors.crimson}/>
            <circle cx="32" cy="38" r="2" fill={colors.gold}/>
          </svg>
        );

      case 'option11':
        // Original Shape + Tap/Touch Gesture Icon
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer gold ring */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner crimson ring */}
            <circle cx="32" cy="32" r="26" stroke={colors.crimson} strokeWidth="2"/>
            {/* Background fill */}
            <circle cx="32" cy="32" r="24" fill={colors.deepBlack}/>
            
            {/* Hand/Finger Tap Gesture (center) */}
            {/* Finger pointing down */}
            <path d="M32 16L32 28" stroke={colors.gold} strokeWidth="3" strokeLinecap="round"/>
            <path d="M28 24L32 28L36 24" stroke={colors.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            
            {/* Tap impact waves */}
            <circle cx="32" cy="36" r="6" stroke={colors.crimson} strokeWidth="2"/>
            <circle cx="32" cy="36" r="10" stroke={colors.crimson} strokeWidth="1.5" opacity="0.6"/>
            <circle cx="32" cy="36" r="14" stroke={colors.crimson} strokeWidth="1" opacity="0.3"/>
            
            {/* Center tap point */}
            <circle cx="32" cy="36" r="3" fill={colors.gold}/>
          </svg>
        );

      case 'option12':
        // Original Shape + NFC Chip/Technology Symbol
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer gold ring */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner crimson ring */}
            <circle cx="32" cy="32" r="26" stroke={colors.crimson} strokeWidth="2"/>
            {/* Background fill */}
            <circle cx="32" cy="32" r="24" fill={colors.deepBlack}/>
            
            {/* NFC Chip Icon (center) */}
            {/* Chip outline */}
            <rect x="22" y="22" width="20" height="20" rx="2" 
                  fill="none" stroke={colors.gold} strokeWidth="2"/>
            
            {/* Chip pins/connectors */}
            <line x1="22" y1="26" x2="18" y2="26" stroke={colors.gold} strokeWidth="1.5"/>
            <line x1="22" y1="32" x2="18" y2="32" stroke={colors.gold} strokeWidth="1.5"/>
            <line x1="22" y1="38" x2="18" y2="38" stroke={colors.gold} strokeWidth="1.5"/>
            <line x1="42" y1="26" x2="46" y2="26" stroke={colors.gold} strokeWidth="1.5"/>
            <line x1="42" y1="32" x2="46" y2="32" stroke={colors.gold} strokeWidth="1.5"/>
            <line x1="42" y1="38" x2="46" y2="38" stroke={colors.gold} strokeWidth="1.5"/>
            
            {/* Inner circuit pattern */}
            <circle cx="32" cy="32" r="6" stroke={colors.crimson} strokeWidth="1.5"/>
            <line x1="26" y1="32" x2="29" y2="32" stroke={colors.crimson} strokeWidth="1"/>
            <line x1="35" y1="32" x2="38" y2="32" stroke={colors.crimson} strokeWidth="1"/>
            <line x1="32" y1="26" x2="32" y2="29" stroke={colors.crimson} strokeWidth="1"/>
            <line x1="32" y1="35" x2="32" y2="38" stroke={colors.crimson} strokeWidth="1"/>
            
            {/* Center dot */}
            <circle cx="32" cy="32" r="2" fill={colors.crimson}/>
          </svg>
        );

      case 'option13':
        // Original Shape + Wireless Signal Waves
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer gold ring */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner crimson ring */}
            <circle cx="32" cy="32" r="26" stroke={colors.crimson} strokeWidth="2"/>
            {/* Background fill */}
            <circle cx="32" cy="32" r="24" fill={colors.deepBlack}/>
            
            {/* Wireless Signal Waves (center) */}
            {/* Left waves */}
            <path d="M20 32C20 32 24 28 24 32C24 36 20 32 20 32" 
                  stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            <path d="M16 32C16 32 22 24 22 32C22 40 16 32 16 32" 
                  stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
            
            {/* Right waves */}
            <path d="M44 32C44 32 40 28 40 32C40 36 44 32 44 32" 
                  stroke={colors.gold} strokeWidth="2" strokeLinecap="round"/>
            <path d="M48 32C48 32 42 24 42 32C42 40 48 32 48 32" 
                  stroke={colors.gold} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
            
            {/* Center radiating circles */}
            <circle cx="32" cy="32" r="4" stroke={colors.crimson} strokeWidth="2"/>
            <circle cx="32" cy="32" r="7" stroke={colors.crimson} strokeWidth="1.5" opacity="0.6"/>
            <circle cx="32" cy="32" r="10" stroke={colors.crimson} strokeWidth="1" opacity="0.3"/>
            
            {/* Center point */}
            <circle cx="32" cy="32" r="2" fill={colors.gold}/>
          </svg>
        );

      case 'option14':
        // Original Shape + Tag Icon with Radio Waves
        return (
          <svg width={logoSize} height={logoSize} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Outer gold ring */}
            <circle cx="32" cy="32" r="30" stroke={colors.gold} strokeWidth="2.5"/>
            {/* Inner crimson ring */}
            <circle cx="32" cy="32" r="26" stroke={colors.crimson} strokeWidth="2"/>
            {/* Background fill */}
            <circle cx="32" cy="32" r="24" fill={colors.deepBlack}/>
            
            {/* NFC Tag Icon (center) */}
            {/* Tag shape */}
            <path d="M22 26L32 16L42 26L42 38C42 40 40 42 38 42L26 42C24 42 22 40 22 38L22 26Z" 
                  fill="none" stroke={colors.gold} strokeWidth="2" strokeLinejoin="round"/>
            
            {/* Tag hole */}
            <circle cx="32" cy="22" r="2.5" fill={colors.crimson}/>
            
            {/* Radio waves emanating from tag */}
            <path d="M26 32C26 32 28 30 32 30C36 30 38 32 38 32" 
                  stroke={colors.crimson} strokeWidth="1.5" strokeLinecap="round"/>
            <path d="M24 36C24 36 27 33 32 33C37 33 40 36 40 36" 
                  stroke={colors.crimson} strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
            <path d="M28 40C28 40 30 38 32 38C34 38 36 40 36 40" 
                  stroke={colors.crimson} strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
          </svg>
        );

      default:
        // Default: Use image or icon
        return src ? (
          <AppImage src={src} alt="Logo" width={size} height={size} className="flex-shrink-0" />
        ) : (
          <AppIcon name={iconName} size={size} className="flex-shrink-0" />
        );
    }
  };

  return (
    <div
      className={`flex items-center gap-2 ${onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
      onClick={onClick}
    >
      {renderCustomLogo()}

      {/* Show text if provided */}
      {text && <span className="text-xl font-bold">{text}</span>}
    </div>
  );
}

export default AppLogo;
